import type { ApiCartException } from '../../exceptions/exceptionDefinitions';
import type { AxiosException } from '../clients/localApiAxiosErrorInterceptors';

import { appConfig } from '../../../../appConfig';
import { createDefaultException } from '../../exceptions/exceptionUtils';
import {
    type ApiHybrisBaseError,
    type ApiValidateCartError,
    type HybrisApiError,
    isApiHybrisBaseError,
} from '../clients/hybrisErrorInterceptor';

export const validateCartErrorInterceptor = (
    error: AxiosException<HybrisApiError<ApiValidateCartError>>,
) => {
    if (!Array.isArray(error?.response.data.errors)) {
        return null;
    }

    const firstError = error.response.data.errors[0];
    switch (firstError.errorCode) {
        // this errorcode is returned by the validation endpoint, to force it, login with punchout, go to last step, empty cart in another window, click "skicka varukorg"
        // https://external-qa.api.coop.se/ecommerce/coop/users/8000002/carts/current/validation
        case 'TOTAL_NOT_VALID':
            return createDefaultException(
                'Av någon anledning är ditt ordervärde för lågt för att lägga en order. Fyll gärna på med något mer du behöver för att uppnå gränsen!',
                firstError.errorCode,
            );
        default:
            return null;
    }
};

export const cartVoucherErrorInterceptor = (
    error: AxiosException<HybrisApiError<ApiHybrisBaseError>>,
) => {
    if (!Array.isArray(error?.response.data.errors)) {
        return null;
    }

    const firstError = error.response.data.errors[0];

    switch (firstError.message) {
        // this errorcode is returned by the validation endpoint, to force it, login with punchout, go to last step, empty cart in another window, click "skicka varukorg"
        // https://external-qa.api.coop.se/ecommerce/coop/users/8000002/carts/current/validation
        case 'couponinvalid':
            return createDefaultException(
                'Koden du har angett är inte giltig. Vänligen försök med en annan kod.',
                'couponinvalid',
            );
        case 'coupon.invalid.code.provided':
            return createDefaultException(
                'Kontrollera att rabattkoden stämmer.',
                'coupon.invalid.code.provided',
            );
        case 'couponalreadyexistscart':
            return createDefaultException(
                'Kampanjkoden finns redan i din varukorg.',
                'couponalreadyexistscart',
            );
        case 'coupon.not.active.expired':
            return createDefaultException(
                'Koden du har angett gäller inte längre. Vänligen försök med en annan kod.',
                'coupon.not.active.expired',
            );
        case 'coupon.already.redeemed':
            return createDefaultException(
                'Koden du har angett har redan lösts in.',
                'coupon.already.redeemed',
            );
        case 'couponinvalidpostcode':
            return createDefaultException(
                'Koden är inte giltig för den valda butiken.',
                'couponinvalidpostcode',
            );
        case 'coupononealreadyincart':
            return createDefaultException(
                'Den här rabattkoden kan inte kombineras med andra. Avbryt och ta bort andra koder om du vill använda denna kod.',
                'coupononealreadyincart',
            );
        default:
            return createDefaultException(
                'Ett fel inträffade när vi försökte lägga till rabattkoden. Vänligen försök igen senare.',
                'unknown',
            );
    }
};

export const updateItemQuantityInterceptor = (
    error: AxiosException<HybrisApiError<ApiHybrisBaseError>>,
): ApiCartException | null => {
    if (!Array.isArray(error?.response.data.errors)) {
        return null;
    }

    const hybrisErrors = error.response.data.errors;

    for (let i = 0; i < hybrisErrors.length; i += 1) {
        const hybrisError = hybrisErrors[i];
        if (isApiHybrisBaseError(hybrisError)) {
            let errorMessage: string | null = null;

            switch (hybrisError.reason) {
                case 'restrictedByUserGroupAndCategory':
                    errorMessage = appConfig.coopUserSettings.punchout.isPunchout
                        ? appConfig.coopSettings.ecommerce.errorMessage
                              .productRestrictedForBusinessUnit
                        : appConfig.coopSettings.ecommerce.errorMessage
                              .productRestrictedForPrivateUser;
                    break;
                case 'unavailable':
                    errorMessage = appConfig.coopSettings.ecommerce.errorMessage.productNotFound;
                    break;
                case 'foodBagOnly':
                    break;
                default:
                    break;
            }

            if (errorMessage) {
                return <ApiCartException>{
                    code: null,
                    friendlyMessage: errorMessage,
                    type: 'Cart',
                };
            }
        }
    }

    return null; // didnt find any known errors, pass over to another interceptor
};
